import { getLambdaResultIssue } from './lambdaResultUtils';

/** Safe fields extracted by the backend from a Townsend validation response. */
export interface TownsendValidationFailure {
  propertyId: string;
  invalidValue?: string;
  optionSetId?: string;
  allowedValues?: string[];
  failureReason?: string;
}

export interface ParsedTownsendError {
  userMessage: string;
  validationFailures?: TownsendValidationFailure[];
  isValidationException: boolean;
  isProgramNotFound: boolean;
  hadSuppressedNoise: boolean;
}

export const TOWNSEND_VALIDATION_FAILED = 'TOWNSEND_VALIDATION_FAILED';
export const PROGRAM_NOT_FOUND_IN_TOWNSEND = 'PROGRAM_NOT_FOUND_IN_TOWNSEND';

const REQUEST_FAILED_MESSAGE =
  "Verity couldn't complete the part number request. Please try again, or contact the Verity team if it keeps happening.";
const VALIDATION_FAILED_MESSAGE =
  "Townsend rejected the part number request due to a validation error. Please check the SKU's regions / marketplaces and try again.";
const PROGRAM_NOT_FOUND_MESSAGE =
  'Program was not found in Townsend. Verify the program name and its Townsend configuration, then retry.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Accept only the structured contract; never stringify malformed fields or parse remote diagnostics. */
const readFailure = (value: unknown): TownsendValidationFailure | null => {
  if (!isRecord(value) || typeof value.propertyId !== 'string' || !value.propertyId.trim()) return null;

  return {
    propertyId: value.propertyId.trim(),
    invalidValue: typeof value.invalidValue === 'string' ? value.invalidValue : undefined,
    optionSetId: typeof value.optionSetId === 'string' ? value.optionSetId : undefined,
    allowedValues:
      Array.isArray(value.allowedValues) && value.allowedValues.every((item) => typeof item === 'string')
        ? value.allowedValues
        : undefined,
    failureReason: typeof value.failureReason === 'string' ? value.failureReason : undefined,
  };
};

/** The website owns the user-facing sentences for structured validation failures. */
const formatFailure = (failure: TownsendValidationFailure): string => {
  const parts: string[] = [];
  if (failure.invalidValue !== undefined) {
    parts.push(`${failure.propertyId} '${failure.invalidValue}' is not accepted by Townsend`);
  } else {
    parts.push(`${failure.propertyId} was rejected by Townsend`);
  }
  if (failure.failureReason) {
    parts[parts.length - 1] += ` (${failure.failureReason})`;
  }
  if (failure.allowedValues && failure.allowedValues.length > 0) {
    parts.push(`Allowed values: ${failure.allowedValues.join(', ')}.`);
  }
  const joined = parts.join('. ');
  return joined.endsWith('.') ? joined : `${joined}.`;
};

/**
 * Present a sanitized request_pn result. Classification comes from the backend's
 * error code, including validation responses whose details could not be extracted.
 * Raw exception strings and malformed responses receive a safe generic message.
 */
export const parseTownsendError = (result: unknown): ParsedTownsendError => {
  const fallback: ParsedTownsendError = {
    userMessage: REQUEST_FAILED_MESSAGE,
    isValidationException: false,
    isProgramNotFound: false,
    hadSuppressedNoise: false,
  };
  if (!isRecord(result)) return fallback;

  if (result.erroredCode === TOWNSEND_VALIDATION_FAILED) {
    const failures = Array.isArray(result.validationFailures)
      ? result.validationFailures
          .map(readFailure)
          .filter((failure): failure is TownsendValidationFailure => failure !== null)
      : [];
    return {
      userMessage:
        failures.length > 0
          ? `Townsend rejected the part number request. ${failures.map(formatFailure).join(' ')}`
          : VALIDATION_FAILED_MESSAGE,
      validationFailures: failures,
      isValidationException: true,
      isProgramNotFound: false,
      hadSuppressedNoise: result.hadSuppressedNoise === true,
    };
  }

  if (result.erroredCode === PROGRAM_NOT_FOUND_IN_TOWNSEND) {
    return { ...fallback, userMessage: PROGRAM_NOT_FOUND_MESSAGE, isProgramNotFound: true };
  }

  if (result.erroredCode != null && result.erroredCode !== '') return fallback;

  // Other valid resolver results carry authored, sanitized business messages.
  // Transport/GraphQL exceptions never enter this branch; the service passes no
  // result for those failures, so arbitrary SDK text is retained only in logs.
  if (typeof result.statusCode === 'number' && typeof result.error === 'string') {
    const issue = getLambdaResultIssue({ error: result.error });
    if (issue) return { ...fallback, userMessage: issue.message };
  }
  return fallback;
};
